import { Box, Typography, useTheme, Grid } from '@mui/material';
import React, { ChangeEvent, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import ButtonGroup from '../../../components/ButtonGroup/ButtonGroup';
import CloseIcon from '../../../components/Icons/CloseIcon';
import { UploadIcon2x } from '../../../components/Icons/UploadIcon';
import { BulkImportMaxFileSize } from '../../../configs/PortalConfig';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import useImportFileStep from './useImportFileStep';
import InfoIcon from '../../../components/Icons/InfoIcon';
import ExcelIcon from '../../../components/Icons/ExcelIcon';
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import ImportDrawer from './ImportDrawer';
import SuccessTickIcon from '../../../components/Icons/SuccessTickIcon';
import ArrowLeft from '../../../components/Icons/ArrowLeft';

function ImportFile() {
  const [t] = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const snackBar = useSnackBar();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isLoading, uploadFile } = useImportFileStep();
  const navigate = useNavigate();
  const theme = useTheme();

  /**
   * Handle navigation to import page
   */
  const handleNavigation = () => {
    navigate('/import');
  };

  const [progress, setProgress] = useState(0);
  const [openDrawer, setOpenDrawer] = useState(false);

  /**
   * Sets the progress of the file upload
   */
  const setProgressFile = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        if (!inputRef.current) {
          clearInterval(interval);
          return 0;
        }
        return prev + 1;
      });
    }, 100);

    return () => clearInterval(interval);
  };

  /**
   * Removes the selected file
   */
  const removeFile = () => {
    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = '';
      setProgress(0);
    }
  };

  /**
   * Handle file drag
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  /**
   * Handle file drop to container
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!event.dataTransfer.files || file) {
      return;
    }
    const droppedFile = event.dataTransfer.files[0];
    const fileExtension = droppedFile.name.toLowerCase().split('.').pop();
    if (fileExtension !== 'csv' && fileExtension !== 'xlsx') {
      snackBar.showSnackBar(t('common.errors.httpReason.23'), 'error');
      removeFile();
      return;
    }
    setProgressFile();

    if (droppedFile.size > BulkImportMaxFileSize * 1000000) {
      snackBar.showSnackBar(t('common.inputValidations.maxSize', { val: `${BulkImportMaxFileSize}mb` }), 'error');
      removeFile();
      return;
    }
    setFile(droppedFile);
  };

  /**
   * Simulates the click on the hidden input by clicking on the displayed button
   */
  const handleUploadClick = () => {
    inputRef.current?.click();
    setProgressFile();
  };

  /**
   * Upload the selected file
   */
  const handleFileUpload = () => {
    if (file) {
      uploadFile(file, () => {
        setOpenDrawer(true);
      });
    }
  };

  /**
   * Handles file change
   * @param e
   */
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    if (e.target.files[0].size > BulkImportMaxFileSize * 1000000) {
      snackBar.showSnackBar(t('common.inputValidations.maxSize', { val: `${BulkImportMaxFileSize}mb` }), 'error');
      removeFile();
      return;
    }
    setFile(e.target.files[0]);
  };

  /**
   * Handles file size format
   * @param bytes
   */
  const formatFileSize = (bytes: number) => {
    const KB = 1024;
    const MB = KB * 1024;

    if (bytes >= MB) {
      return `${(bytes / MB).toFixed(2)} MB`;
    }
    if (bytes >= KB) {
      return `${(bytes / KB).toFixed(2)} KB`;
    }
    return `${bytes} B`;
  };

  return (
    <Grid container sx={{ paddingBottom: '2rem' }}>
      <Grid item xl={6.5} md={9} xs={12}>
        <Breadcrumbs
          activeLinks={[{ label: t('navbar.import'), url: `/import` }]}
          disabledLinks={[t('pages.importFile.title')]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', marginTop: '1rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArrowLeft stroke={theme.palette.primary.main} style={{ cursor: 'pointer' }} onClick={handleNavigation} />
            <Typography
              sx={{ fontWeight: '700', fontSize: '1.25rem', marginLeft: '0.5rem' }}
              variant="body1"
              lineHeight="normal"
            >
              {t('pages.importFile.uploadDescription')}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '1.125rem',
              color: theme.palette.monochrome.monoChrome6,
              paddingTop: '1rem',
              marginBottom: '2.5rem',
            }}
            lineHeight="normal"
          >
            <Trans>{t('pages.importFile.uploadHelper')}</Trans>
          </Typography>
          {/* File upload */}
          {file ? (
            <Box sx={{ padding: '16px 0' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
              >
                {progress === 100 ? <SuccessTickIcon width={36} /> : <ExcelIcon />}
                <Box sx={{ flex: 1, marginLeft: '8px' }}>
                  <Box sx={{ fontWeight: 'bold' }}>{file.name}</Box>
                  <Box sx={{ color: '#888', fontSize: '12px' }}>{formatFileSize(file.size)}</Box>
                </Box>
                <CloseIcon
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                />
              </Box>
              <Box
                sx={{
                  height: '8px',
                  marginLeft: '6%',
                  width: '94%',
                  backgroundColor: '#eee',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${progress}%`,
                    backgroundColor: '#4caf50',
                  }}
                />
              </Box>
            </Box>
          ) : null}
          <Box>
            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                height: '11rem',
                border: `2px dashed ${theme.palette.monochrome.monoChrome4}`,
                borderRadius: '0.375rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                backgroundColor: theme.palette.monochrome.monoChrome2,
                color: theme.palette.monochrome.monoChrome6,
                fontSize: '20px',
                marginBottom: '20px',
                opacity: file ? '38%' : '100%',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <UploadIcon2x />
                <Typography sx={{ fontSize: '20px', paddingTop: '1.25rem' }}>
                  {t('pages.importFile.dragFiles')}
                </Typography>
                <Typography
                  sx={{
                    cursor: file ? 'auto' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '18px',
                    color: theme.palette.primary.main,
                    paddingTop: '0.5rem',
                  }}
                  onClick={file ? undefined : handleUploadClick}
                >
                  {t('pages.importFile.selectFile')}
                </Typography>
              </Box>
            </Box>

            <input
              type="file"
              ref={inputRef}
              accept=".csv,.xlsx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              data-testid="fileInput"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
              <InfoIcon />
              <Typography sx={{ fontSize: '20px', paddingLeft: '0.5rem' }}>
                {t('pages.importFile.uploadInfo')}
              </Typography>
            </Box>
          </Box>
          {/* Buttons */}
          <Box sx={{ marginTop: '2rem', marginBottom: '5rem' }}>
            <ButtonGroup>
              <Button
                label={t('common.cancel')}
                color="alternative2Button"
                onClick={handleNavigation}
                disabled={isLoading}
              />
              <Button
                label={t('common.submit')}
                loading={isLoading}
                color="primaryButton"
                onClick={handleFileUpload}
                disabled={!file?.name || isLoading || progress < 100}
              />
            </ButtonGroup>
          </Box>
        </Box>
        {openDrawer ? (
          <ImportDrawer
            onClose={() => {
              setOpenDrawer(false);
            }}
            open={openDrawer}
          />
        ) : null}
      </Grid>
    </Grid>
  );
}

export default ImportFile;
